/* Céline Dion — Paris 2026-2027 | theme scripts
   No jQuery. Progressive enhancement only: every feature below degrades to a
   working server-rendered form if JS fails. */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     Mobile navigation
     --------------------------------------------------------------------- */
  function initNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var nav = document.querySelector('[data-nav]');
    if (!toggle || !nav) return;

    toggle.addEventListener('click', function () {
      var open = nav.getAttribute('data-open') === 'true';
      nav.setAttribute('data-open', String(!open));
      toggle.setAttribute('aria-expanded', String(!open));
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      nav.setAttribute('data-open', 'false');
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  /* ---------------------------------------------------------------------
     Sticky header
     An IntersectionObserver on a one-pixel sentinel, not a scroll listener:
     no work happens on the main thread while the user scrolls.
     --------------------------------------------------------------------- */
  function initStickyHeader() {
    var header = document.querySelector('[data-header]');
    if (!header || !header.classList.contains('site-header--transparent')) return;
    if (!('IntersectionObserver' in window)) return;

    var sentinel = document.createElement('div');
    sentinel.className = 'site-header__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    var hero = document.querySelector('.hero');
    if (!hero) return;
    hero.appendChild(sentinel);

    new IntersectionObserver(
      function (entries) {
        header.classList.toggle('site-header--stuck', !entries[0].isIntersecting);
      },
      { rootMargin: '0px' }
    ).observe(sentinel);
  }

  /* ---------------------------------------------------------------------
     Date rows
     Opening one closes the others, so the list never becomes a wall of open
     panels the user has to scroll past to reach the next date.
     --------------------------------------------------------------------- */
  function initDateAccordion() {
    var list = document.querySelector('.dates');
    if (!list) return;

    var rows = list.querySelectorAll('details');
    Array.prototype.forEach.call(rows, function (row) {
      row.addEventListener('toggle', function () {
        if (!row.open) return;

        /* La date ouverte est mesuree avant que les autres ne se referment :
           replier un panneau situe plus haut raccourcit la page de plusieurs
           centaines de pixels, et la date qu'on vient de choisir partait hors
           de l'ecran. On la remet exactement ou elle etait. */
        var summary = row.querySelector('summary');
        var before = summary ? summary.getBoundingClientRect().top : null;

        Array.prototype.forEach.call(rows, function (other) {
          if (other !== row) other.open = false;
        });

        if (before !== null) {
          var after = summary.getBoundingClientRect().top;
          var drift = after - before;
          if (Math.abs(drift) > 1) window.scrollBy(0, drift);
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
     Tour measurement

     Two events, both pushed to window.dataLayer so GA4, Meta or any GTM tag
     can consume them:

       tour_date_open   — someone expanded a date. This is the demand signal:
                          it tells you which nights people are considering,
                          including the ones that are sold out, which is the
                          number worth having when planning extra dates.
       tour_seller_click — someone left for an authorized seller. With the
                          date, the seller and the placement, this is the
                          closest thing to an attributed conversion available
                          when checkout happens on someone else's domain.

     Sold-out dates still emit tour_date_open, so unmet demand is countable.
     --------------------------------------------------------------------- */
  function push(event, payload) {
    window.dataLayer = window.dataLayer || [];
    payload = payload || {};
    payload.event = event;
    window.dataLayer.push(payload);
  }

  function initTourTracking() {
    var section = document.querySelector('.tour');
    if (!section) return;

    var opened = {};

    Array.prototype.forEach.call(section.querySelectorAll('details[data-track-date]'), function (row) {
      row.addEventListener('toggle', function () {
        if (!row.open) return;

        var date = row.getAttribute('data-track-date');
        if (opened[date]) return;
        opened[date] = true;

        var bands = Array.prototype.map.call(
          row.querySelectorAll('[data-track-band]'),
          function (band) {
            return {
              category: band.getAttribute('data-track-band'),
              price: band.getAttribute('data-track-price'),
              sku: band.getAttribute('data-track-sku') || undefined
            };
          }
        );

        // L'id produit est la cle de jointure avec les rapports Shopify : la
        // meme date porte le meme identifiant des deux cotes.
        var li = row.closest('[data-track-product]');

        push('tour_date_open', {
          show_date: date,
          show_label: row.getAttribute('data-track-label'),
          availability: row.getAttribute('data-track-status'),
          product_id: li ? li.getAttribute('data-track-product') : undefined,
          product_handle: li ? li.getAttribute('data-track-handle') : undefined,
          categories: bands
        });
      });
    });

    section.addEventListener('click', function (event) {
      var link = event.target.closest('[data-track-seller]');
      if (!link) return;

      var row = link.closest('[data-track-product]');

      push('tour_seller_click', {
        seller: link.getAttribute('data-track-seller'),
        show_date: link.getAttribute('data-track-date') || 'all_dates',
        placement: link.getAttribute('data-track-placement'),
        product_id: row ? row.getAttribute('data-track-product') : undefined,
        destination: link.href
      });
    });

    /* Une impression de liste, envoyee une fois. Sans elle, on ne connait que
       les dates ouvertes : impossible de calculer un taux d'ouverture par date,
       donc impossible de dire qu'une date interesse peu ou qu'elle est juste
       mal placee dans la liste. */
    if (section.hasAttribute('data-tour-products')) {
      var items = Array.prototype.map.call(
        section.querySelectorAll('[data-track-product]'),
        function (row, i) {
          var details = row.querySelector('details');
          return {
            item_id: row.getAttribute('data-track-product'),
            item_name: row.getAttribute('data-track-handle'),
            item_list_name: 'tour_dates',
            index: i + 1,
            availability: details ? details.getAttribute('data-track-status') : 'closed'
          };
        }
      );
      if (items.length) push('view_item_list', { item_list_name: 'tour_dates', items: items });
    }
  }

  /* ---------------------------------------------------------------------
     Variant picker
     Reads the variant map printed by the product section, keeps the URL,
     price, availability and hidden variant id in sync.
     --------------------------------------------------------------------- */
  function initVariantPicker() {
    var root = document.querySelector('[data-variant-picker]');
    if (!root) return;

    var dataEl = document.querySelector('[data-variant-json]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }

    var idInput = root.querySelector('[name="id"]');
    var submit = root.querySelector('[data-add-to-cart]');
    var priceTarget = document.querySelector('[data-price-target]');
    var soldOutText = root.getAttribute('data-sold-out-text') || 'Sold out';
    var addText = root.getAttribute('data-add-text') || 'Add to cart';
    var moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';

    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }

    function selectedOptions() {
      var checked = root.querySelectorAll('input[type="radio"]:checked, select[data-option-select]');
      return Array.prototype.map.call(checked, function (el) {
        return el.value;
      });
    }

    function matchVariant(options) {
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var match = true;
        for (var j = 0; j < options.length; j++) {
          if (variant.options[j] !== options[j]) {
            match = false;
            break;
          }
        }
        if (match) return variant;
      }
      return null;
    }

    function update() {
      var variant = matchVariant(selectedOptions());

      if (!variant) {
        if (submit) {
          submit.disabled = true;
          submit.textContent = soldOutText;
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceTarget) {
        priceTarget.textContent = formatMoney(variant.price);
      }

      if (submit) {
        submit.disabled = !variant.available;
        submit.textContent = variant.available ? addText : soldOutText;
      }

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    root.addEventListener('change', update);
    update();
  }

  /* ---------------------------------------------------------------------
     Cart line quantity — auto-submits so there is no "update" button to miss
     --------------------------------------------------------------------- */
  function initCartQuantity() {
    var inputs = document.querySelectorAll('[data-cart-quantity]');
    Array.prototype.forEach.call(inputs, function (input) {
      input.addEventListener('change', function () {
        var form = input.closest('form');
        if (form) form.submit();
      });
    });
  }

  /* ---------------------------------------------------------------------
     Boutons d'action d'une date
     --------------------------------------------------------------------- */

  /* Un seul ecouteur delegue : les boutons apparaissent dans la liste, sur la
     fiche et dans la barre collante, et rien ne garantit qu'ils soient tous
     dans le DOM au chargement. */
  function initEventCta() {
    document.addEventListener('click', function (event) {
      /* Le lien produit d'un panneau de date : sans cet evenement, on saurait
         qu'une date interesse mais pas qu'elle vend du merch. */
      var related = event.target.closest('[data-track-related]');
      if (related) {
        push('related_product_click', {
          kind: related.getAttribute('data-track-related'),
          show_date: related.getAttribute('data-track-date') || undefined,
          item_id: related.getAttribute('data-track-item') || undefined,
          destination: related.href || undefined
        });
      }

      var link = event.target.closest('[data-track-cta]');
      if (!link) return;

      var group = link.closest('[data-event-cta]');
      push('event_cta_click', {
        cta: link.getAttribute('data-track-cta'),
        seller: link.getAttribute('data-track-seller') || undefined,
        show_date: group ? group.getAttribute('data-track-date') : undefined,
        product_id: group ? group.getAttribute('data-track-product') : undefined,
        placement: group ? group.getAttribute('data-track-placement') : undefined,
        destination: link.href || undefined
      });
    });
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /* Format ICS : UTC compact, sans separateurs. */
  function icsStamp(date) {
    return date.getUTCFullYear() + pad(date.getUTCMonth() + 1) + pad(date.getUTCDate()) + 'T' +
      pad(date.getUTCHours()) + pad(date.getUTCMinutes()) + '00Z';
  }

  /* Une ligne ICS ne doit pas depasser 75 octets ; les plis se font avec un
     retour a la ligne suivi d'une espace. Sans ca, une longue adresse casse le
     fichier dans Outlook. */
  function icsFold(line) {
    var out = '';
    while (line.length > 73) {
      out += line.slice(0, 73) + '\r\n ';
      line = line.slice(73);
    }
    return out + line;
  }

  function icsEscape(text) {
    return String(text || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  }

  function initAddToCalendar() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-add-to-calendar]');
      if (!button) return;

      var start = new Date(button.getAttribute('data-start'));
      if (isNaN(start.getTime())) return;

      var minutes = parseInt(button.getAttribute('data-duration'), 10) || 150;
      var end = new Date(start.getTime() + minutes * 60000);
      var title = button.getAttribute('data-title');
      var url = button.getAttribute('data-url') || location.href;

      var lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//' + location.hostname + '//Tour dates//FR',
        'CALSCALE:GREGORIAN',
        'BEGIN:VEVENT',
        icsFold('UID:' + start.getTime() + '@' + location.hostname),
        'DTSTAMP:' + icsStamp(new Date()),
        'DTSTART:' + icsStamp(start),
        'DTEND:' + icsStamp(end),
        icsFold('SUMMARY:' + icsEscape(title)),
        icsFold('LOCATION:' + icsEscape(button.getAttribute('data-location'))),
        icsFold('URL:' + url),
        'END:VEVENT',
        'END:VCALENDAR'
      ];

      // « Céline » slugue en « c-line » si on retire les accents sans les
      // decomposer d'abord.
      var slug = title
        .normalize ? title.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : title;
      slug = slug.replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();

      var blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
      var href = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = href;
      a.download = (slug || 'event') + '.ics';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);

      var group = button.closest('[data-event-cta]');
      push('add_to_calendar', {
        show_date: group ? group.getAttribute('data-track-date') : undefined,
        product_id: group ? group.getAttribute('data-track-product') : undefined,
        placement: group ? group.getAttribute('data-track-placement') : undefined
      });
    });
  }

  /* La barre n'apparait qu'une fois le bouton principal sorti de l'ecran :
     afficher les deux en meme temps ne fait que voler de la hauteur utile. */
  function initEventBar() {
    var bar = document.querySelector('[data-event-bar]');
    if (!bar) return;

    var anchor = document.querySelector('.ticket [data-event-cta]');
    if (!anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      bar.hidden = entries[0].isIntersecting;
    }, { rootMargin: '-80px 0px 0px 0px' });

    observer.observe(anchor);
  }

  /* ---------------------------------------------------------------------
     Categorie de billet : liste des dates -> fiche de date
     --------------------------------------------------------------------- */

  /* La liste de l'accueil est saisie a la main et ne connait aucun identifiant
     de variante : elle passe donc ?category=<nom>. Ici on le traduit en
     variante, et l'URL est reecrite en ?variant= pour qu'un partage de lien
     retombe sur le meme billet, resolu cette fois cote serveur. */
  function applyTicketOption(option, replaceUrl) {
    if (!option) return;

    var ticket = document.querySelector('[data-digital-ticket]');
    if (ticket) {
      var category = ticket.querySelector('[data-ticket-category]');
      var price = ticket.querySelector('[data-ticket-price]');
      var serial = ticket.querySelector('[data-ticket-serial]');
      if (category) category.textContent = option.getAttribute('data-category');
      if (price) price.textContent = option.getAttribute('data-price');
      if (serial && option.getAttribute('data-sku')) {
        serial.textContent = option.getAttribute('data-sku');
      }
    }

    var all = document.querySelectorAll('[data-ticket-option]');
    Array.prototype.forEach.call(all, function (item) {
      var row = item.closest('.dates__band');
      var isCurrent = item === option;
      if (row) row.classList.toggle('dates__band--chosen', isCurrent);
      if (isCurrent) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });

    /* Vente directe : le meme choix pilote le formulaire, sinon la personne
       choisirait deux fois la meme categorie. */
    var radio = document.querySelector(
      '[data-variant-picker] input[name="option-1"][value="' +
        (window.CSS && CSS.escape ? CSS.escape(option.getAttribute('data-category')) : option.getAttribute('data-category')) +
        '"]'
    );
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (replaceUrl && window.history && history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.delete('category');
      url.searchParams.set('variant', option.getAttribute('data-variant-id'));
      history.replaceState({}, '', url.toString());
    }

    push('ticket_category_select', {
      category: option.getAttribute('data-category'),
      sku: option.getAttribute('data-sku') || undefined,
      variant_id: option.getAttribute('data-variant-id')
    });
  }

  function initTicketOptions() {
    var options = document.querySelectorAll('[data-ticket-option]');
    if (!options.length) return;

    /* Categorie heritee de la liste des dates. */
    var params = new URLSearchParams(window.location.search);
    var wanted = params.get('category');
    if (wanted) {
      var match = null;
      Array.prototype.forEach.call(options, function (option) {
        if (!match && option.getAttribute('data-category') === wanted) match = option;
      });
      if (match) applyTicketOption(match, true);
    }

    /* Changer de categorie sur place : pas de rechargement, mais le lien reste
       un vrai lien pour qui n'a pas de JavaScript. */
    Array.prototype.forEach.call(options, function (option) {
      option.addEventListener('click', function (event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        applyTicketOption(option, true);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Reservation : choix de la categorie dans le panneau d'une date
     --------------------------------------------------------------------- */

  /* Le panneau s'ouvre deja en CSS via :target. Le JS ne fait que deux choses :
     inscrire le choix sur la souche du billet, et le remonter dans le
     dataLayer. Si le JS tombe, la date reste consultable. */
  function initBookingPanels() {
    var root = document.querySelector('[data-booking-panels]');
    if (!root) return;

    root.addEventListener('click', function (event) {
      var band = event.target.closest('[data-pick-category]');
      if (!band) return;

      var panel = band.closest('[data-panel]');
      if (!panel) return;

      var category = band.getAttribute('data-category');
      var price = band.getAttribute('data-price');

      var stub = panel.querySelector('[data-ticket-category]');
      var stubPrice = panel.querySelector('[data-ticket-price]');
      if (stub) stub.textContent = category;
      if (stubPrice) stubPrice.textContent = price;

      var siblings = panel.querySelectorAll('[data-pick-category]');
      Array.prototype.forEach.call(siblings, function (item) {
        item.setAttribute('aria-pressed', String(item === band));
      });

      push('ticket_category_select', {
        category: category,
        price: price,
        show_date: panel.getAttribute('data-panel'),
        placement: 'booking_panel'
      });
    });

    /* Ouvrir une date est un signal d'intention : c'est le geste le plus proche
       d'un achat quand la caisse est ailleurs. */
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-open-date]');
      if (!opener) return;
      push('tour_date_open', {
        show_date: opener.getAttribute('data-open-date'),
        placement: 'calendar'
      });
    });
  }

  /* ---------------------------------------------------------------------
     Liste des dates : la souche du billet suit la categorie cochee
     --------------------------------------------------------------------- */

  /* Le formulaire fonctionne sans JavaScript — il poste vers /cart/add et
     Shopify renvoie au panier. Ceci n'ajoute que le retour visuel. */
  function initDateShop() {
    var section = document.querySelector('[data-dateshop]');
    if (!section) return;

    section.addEventListener('change', function (event) {
      var input = event.target.closest('.dateshop__option input');
      if (!input) return;

      var panel = input.closest('.dates__panel');
      if (!panel) return;

      var stub = panel.querySelector('[data-ticket-category]');
      var price = panel.querySelector('[data-ticket-price]');
      var serial = panel.querySelector('[data-ticket-serial]');
      if (stub) stub.textContent = input.getAttribute('data-category');
      if (price) price.textContent = input.getAttribute('data-price');
      if (serial && input.getAttribute('data-sku')) {
        serial.textContent = input.getAttribute('data-sku');
      }

      /* Le bouton nomme ce qu'on achete : sans ca, il faut remonter verifier
         quelle categorie est cochee avant d'oser cliquer. */
      var detail = panel.querySelector('[data-cta-detail]');
      if (detail) {
        detail.textContent = input.getAttribute('data-category') + ' · ' + input.getAttribute('data-price');
      }

      push('ticket_category_select', {
        category: input.getAttribute('data-category'),
        sku: input.getAttribute('data-sku') || undefined,
        variant_id: input.value,
        show_date: input.closest('form') ? input.closest('form').getAttribute('data-date') : undefined
      });
    });

    /* L'ajout au panier est l'evenement le plus proche d'un achat : on le
       remonte avant que le navigateur ne quitte la page. */
    section.addEventListener('submit', function (event) {
      var form = event.target.closest('.dateshop__form');
      if (!form) return;
      var checked = form.querySelector('input[name="id"]:checked');
      push('add_to_cart', {
        show_date: form.getAttribute('data-date'),
        variant_id: checked ? checked.value : undefined,
        category: checked ? checked.getAttribute('data-category') : undefined,
        sku: checked ? checked.getAttribute('data-sku') : undefined
      });
    });

    /* La souche affiche la categorie cochee des l'ouverture. */
    Array.prototype.forEach.call(section.querySelectorAll('.dateshop__form'), function (form) {
      var checked = form.querySelector('input[name="id"]:checked');
      if (checked) checked.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------------------------------------------------------------------
     Partager une date
     --------------------------------------------------------------------- */

  /* Le partage natif quand le navigateur le propose, la copie du lien sinon.
     L'URL porte l'ancre de la date : celui qui recoit le lien atterrit dessus,
     pas en haut d'une liste de vingt-six soirs. */
  function initShare() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-share]');
      if (!button) return;

      var url = button.getAttribute('data-share-url');
      var title = button.getAttribute('data-share-title');

      push('share_date', { show_date: url.split('#d-')[1], method: navigator.share ? 'native' : 'copy' });

      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(function () {});
        return;
      }

      function done() {
        var original = button.textContent;
        button.textContent = button.getAttribute('data-copied');
        button.setAttribute('data-state', 'done');
        setTimeout(function () {
          button.textContent = original;
          button.removeAttribute('data-state');
        }, 2000);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, function () {});
      } else {
        /* Repli pour les navigateurs sans presse-papiers moderne. */
        var field = document.createElement('input');
        field.value = url;
        document.body.appendChild(field);
        field.select();
        try { document.execCommand('copy'); done(); } catch (e) {}
        document.body.removeChild(field);
      }
    });
  }

  function init() {
    initNav();
    initStickyHeader();
    initDateAccordion();
    initTourTracking();
    initEventCta();
    initAddToCalendar();
    initEventBar();
    initTicketOptions();
    initBookingPanels();
    initDateShop();
    initShare();
    initVariantPicker();
    initCartQuantity();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
